import { useState, useEffect } from "react";
import { API_BASE } from "../../utils/api";
import InfoScreen from "./InfoScreen";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const navigate = useNavigate();

  // Parse the access_token from the URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      if (error) {
        setGlobalError(errorDescription || "Something went wrong.");
        return;
      }
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");
      if (accessToken) {
        setToken(accessToken);
      } else {
        setGlobalError("Missing token. Cannot reset password.");
        return;
      }

      if (refreshToken) {
        setRefreshToken(refreshToken);
      } else {
        setGlobalError("Missing refresh token. Cannot reset password.");
        return;
      }
    } else {
      navigate("/")
    }
  }, [navigate]);

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!token || !refreshToken) {
      setError("Missing token. Cannot reset password.");
      setLoading(false);
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill out both password fields.");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_password: newPassword,
          refresh_token: refreshToken,
        }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "Failed to reset password.");
      }

      setSuccess(json.message);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // If there's a global error, show info screen instead of the form
  if (globalError) {
    return (
      <InfoScreen
        title="Well... this sucks"
        description={globalError}
        buttonText="Home"
        buttonLink="/"
      />
    );
  } else if (success) { 
      return <InfoScreen
        title="You're all set!"
        description="Sign into the app with your new password to continue"
        buttonText="Home"
        buttonLink="/"
      />
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-lg px-12">
        <h1 className="text-3xl text-center text-white mb-10">
          Reset Password
        </h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleReset();
          }}
        >
          <div className="mb-6">
            <label className="block text-base font-medium text-white mb-3">
              New Password
            </label>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-5 py-4 bg-card text-white text-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mb-6">
            <label className="block text-base font-medium text-white mb-3">
              Confirm New Password
            </label>
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-5 py-4 bg-card text-white text-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {error && (
            <div className="text-primary text-center mb-7">{error}</div>
          )}
          {success && (
            <div className="text-green-400 text-center mb-7">{success}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-4 px-5 text-lg font-semibold hover:opacity-90 transition disabled:opacity-50 mb-4"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
